import { useGetLatestCalculation } from "../../../../api/versioningService";
import { MAPPING_HEADER_COPY } from "../constants/mappingHeader.constants";
import {
  ClimateImpactState,
  UseMappingHeaderInfoParams,
  UseMappingHeaderInfoResult
} from "../types/mappingHeader.types";

/**
 * Resolves the version being viewed. AC1/AC4 want "Version 0" + "Pending" shown here even
 * though version 0 is otherwise treated as a non-noteworthy baseline elsewhere in the app.
 */
export const useMappingHeaderInfo = ({
  defaultVersion,
  viewedVersion,
  isViewingHistoricalVersion,
  salesUnit
}: UseMappingHeaderInfoParams): UseMappingHeaderInfoResult => {
  const version = isViewingHistoricalVersion ? viewedVersion : defaultVersion;

  const versionLabel = version
    ? version.versionName?.trim() || `${MAPPING_HEADER_COPY.versionLabel} ${version.versionNumber}`
    : null;

  // Only ask for a calculation once the version has one -- otherwise the backend's
  // drawingId fallback can return a *different* version's result (AC7).
  const { data: latestCalculation } = useGetLatestCalculation(
    version?._id || "",
    !!version?.isCalculated
  );

  const climateChange = latestCalculation?.totalImpact?.climateChange;
  const measure = climateChange?.measure;
  const unit =
    climateChange?.unit === "kgCO2eq" ? `${climateChange.unit} /kg` : climateChange?.unit;

  const climateImpact: ClimateImpactState =
    version?.isCalculated && typeof measure === "number"
      ? { status: "ready", measure, unit: unit || MAPPING_HEADER_COPY.defaultImpactUnit }
      : { status: "pending" };

  const productSummaryPath =
    climateImpact.status === "ready" && salesUnit
      ? `/product-summary?salesUnitId=${salesUnit.salesUnitId}&productId=${
          salesUnit.productId
        }&isAverageProduct=${salesUnit.isAverageProduct === "true"}`
      : null;

  return { versionLabel, climateImpact, productSummaryPath };
};
